import React from 'react'
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  Container,
  Link,
  Paper,
  Fade,
  Slide,
  Grid,
} from '@mui/material'
import {
  Email as EmailIcon,
  Help as HelpIcon,
  AccountCircle,
  CalendarMonth,
  Payment,
  Shield,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

const SupportPage: React.FC = () => {
  const navigate = useNavigate()

  const supportTopics = [
    {
      icon: <AccountCircle sx={{ fontSize: 32, color: '#2E7DAF' }} />,
      title: 'Account & Login',
      description:
        'Help with account setup, password reset, profile updates, and login issues.',
    },
    {
      icon: <CalendarMonth sx={{ fontSize: 32, color: '#2E7DAF' }} />,
      title: 'Scheduling & Appointments',
      description:
        'Questions about booking sessions, rescheduling, cancellations.',
    },
    {
      icon: <Payment sx={{ fontSize: 32, color: '#2E7DAF' }} />,
      title: 'Billing & Insurance',
      description:
        'Support for payments, insurance claims, invoices, and billing inquiries.',
    },
    {
      icon: <Shield sx={{ fontSize: 32, color: '#2E7DAF' }} />,
      title: 'Privacy & Security',
      description:
        'Information about data protection, privacy practices, and account security.',
    },
  ]

  const faqItems = [
    {
      question: 'How do I contact support?',
      answer:
        'You can reach our support team at support@PillarThera.com. We typically respond within 1–2 business days.',
    },
    {
      question: 'What types of therapy does PillarThera offer?',
      answer:
        'PillarThera connects you with licensed providers offering Speech Therapy, Occupational Therapy, and Physical Therapy.',
    },
    {
      question: 'How do I reset my password?',
      answer:
        'Visit the login page and click "Forgot Password." Enter your email to receive reset instructions.',
    },
    {
      question: 'Is my health information secure?',
      answer:
        'Yes. We use industry-standard encryption and security measures. See our Privacy Policy for details.',
    },
  ]

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Fade in timeout={800}>
        <AppBar
          position="static"
          elevation={0}
          sx={{
            bgcolor: 'white',
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            height: '80px',
            flexShrink: 0,
          }}
        >
          <Toolbar
            sx={{ justifyContent: 'space-between', py: 1, height: '100%' }}
          >
            <Slide direction="right" in timeout={1000}>
              <Box
                component="img"
                src="/logo.svg"
                alt="PillarThera Logo"
                sx={{
                  height: { xs: 32, sm: 60 },
                  width: 'auto',
                  cursor: 'pointer',
                  transition: 'opacity 0.3s ease',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
                onClick={() => navigate('/')}
              />
            </Slide>
          </Toolbar>
        </AppBar>
      </Fade>

      {/* Main Content */}
      <Box sx={{ flex: 1, py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Fade in timeout={600}>
            <Box>
              {/* Hero */}
              <Box sx={{ textAlign: 'center', mb: 5 }}>
                <HelpIcon sx={{ fontSize: 48, color: '#2E7DAF', mb: 2 }} />
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: 'grey.900',
                    mb: 1,
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                  }}
                >
                  PillarThera Support
                </Typography>
                <Typography
                  sx={{
                    color: 'grey.600',
                    maxWidth: 560,
                    mx: 'auto',
                    lineHeight: 1.6,
                  }}
                >
                  We&apos;re here to help. Find answers to common questions or
                  reach out to our team for assistance.
                </Typography>
              </Box>

              {/* Contact Card */}
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  mb: 5,
                  borderRadius: 2,
                  bgcolor: 'white',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  textAlign: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: 'grey.800',
                    mb: 2,
                    fontSize: '1.125rem',
                  }}
                >
                  Contact Us
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <EmailIcon sx={{ color: 'grey.600', fontSize: 20 }} />
                  <Link
                    href="mailto:support@PillarThera.com"
                    sx={{
                      color: '#2E7DAF',
                      fontWeight: 500,
                      fontSize: '1.0625rem',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    support@PillarThera.com
                  </Link>
                </Box>
                <Typography
                  sx={{
                    color: 'grey.500',
                    mt: 2,
                    fontSize: '0.875rem',
                  }}
                >
                  We typically respond within 1–2 business days.
                </Typography>
              </Paper>

              {/* Support Topics */}
              <Typography
                sx={{
                  fontWeight: 600,
                  color: 'grey.800',
                  mb: 3,
                  fontSize: '1.125rem',
                }}
              >
                How can we help?
              </Typography>
              <Grid container spacing={3} sx={{ mb: 5 }}>
                {supportTopics.map((topic, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        height: '100%',
                        borderRadius: 2,
                        bgcolor: 'white',
                        border: '1px solid',
                        borderColor: 'grey.200',
                        transition: 'border-color 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          borderColor: 'grey.300',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        },
                      }}
                    >
                      <Box sx={{ mb: 1.5 }}>{topic.icon}</Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: 'grey.800',
                          mb: 1,
                          fontSize: '1rem',
                        }}
                      >
                        {topic.title}
                      </Typography>
                      <Typography
                        sx={{
                          color: 'grey.600',
                          fontSize: '0.9375rem',
                          lineHeight: 1.6,
                        }}
                      >
                        {topic.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {/* FAQ */}
              <Typography
                sx={{
                  fontWeight: 600,
                  color: 'grey.800',
                  mb: 3,
                  fontSize: '1.125rem',
                }}
              >
                Frequently Asked Questions
              </Typography>
              <Box sx={{ mb: 4 }}>
                {faqItems.map((item, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: 3,
                      mb: 2,
                      borderRadius: 2,
                      bgcolor: 'white',
                      border: '1px solid',
                      borderColor: 'grey.200',
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: 'grey.800',
                        mb: 1,
                        fontSize: '1rem',
                      }}
                    >
                      {item.question}
                    </Typography>
                    <Typography
                      sx={{
                        color: 'grey.600',
                        fontSize: '0.9375rem',
                        lineHeight: 1.6,
                      }}
                    >
                      {item.answer}
                    </Typography>
                  </Paper>
                ))}
              </Box>

              {/* Privacy Link */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  sx={{
                    color: 'grey.600',
                    fontSize: '0.9375rem',
                    mb: 1,
                  }}
                >
                  For information about how we collect and protect your data,
                  see our{' '}
                  <Link
                    component="button"
                    onClick={() => navigate('/privacy')}
                    sx={{
                      color: '#2E7DAF',
                      fontWeight: 500,
                      textDecoration: 'none',
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Privacy Policy
                  </Link>
                </Typography>
                <Link
                  component="button"
                  onClick={() => navigate('/')}
                  sx={{
                    color: '#2E7DAF',
                    fontWeight: 500,
                    textDecoration: 'none',
                    cursor: 'pointer',
                    fontSize: '0.9375rem',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Back to Home
                </Link>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>
    </Box>
  )
}

export default SupportPage
